import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import AOS from "aos";
import "aos/dist/aos.css";

import { useProperties } from "../../hooks/useProperties";

import BedIcon from "../../assets/img/homeimg/propertylistings-bedicon.svg";
import PersonIcon from "../../assets/img/homeimg/propertylistings-personicon.svg";
import BatIcon from "../../assets/img/homeimg/propertylistings-bat.svg";

import StarColor from "../../assets/img/homeimg/propertylistings-starwithcolor.svg";
import StarWhite from "../../assets/img/homeimg/propertylistings-starwithoutcolor.svg";

import VrIcon from "../../assets/img/homeimg/discover-firsticon2.svg";

const PropertyListings = () => {
  const { properties, loading, error } = useProperties("rent");
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => [searchParams.get("q"), searchParams.get("location")].filter(Boolean).join(" "));
  const [propertyType, setPropertyType] = useState(() => searchParams.get("type") || "");
  const [minBedrooms, setMinBedrooms] = useState("");
  const [maxPrice, setMaxPrice] = useState(() => searchParams.get("maxPrice") || "");
  const [page, setPage] = useState(1);
  const pageSize = 9;

  useEffect(() => {

    AOS.init({
      duration: 800,
      once: true,
    });

  }, []);

  const propertyTypes = useMemo(
    () => [...new Set(properties.map((property) => property.PropertyType?.NameType).filter(Boolean))],
    [properties]
  );

  const filteredProperties = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const minimumBedrooms = Number(minBedrooms);
    const maximumPrice = Number(maxPrice);

    return properties.filter((property) => {
      const searchableText = [
        property.PropertyType?.NameType,
        property.Location,
        property.Province,
        property.Reference,
        property.ShortDescription,
      ].filter(Boolean).join(" ").toLowerCase();
      const price = Number(property.Price) || 0;
      const bedrooms = Number(property.Bedrooms) || 0;

      return (
        (!normalizedSearch || searchableText.includes(normalizedSearch)) &&
        (!propertyType || property.PropertyType?.NameType?.toLowerCase().includes(propertyType.toLowerCase())) &&
        (!minBedrooms || bedrooms >= minimumBedrooms) &&
        (!maxPrice || price <= maximumPrice)
      );
    });
  }, [properties, search, propertyType, minBedrooms, maxPrice]);

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / pageSize));
  const visibleProperties = filteredProperties.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, propertyType, minBedrooms, maxPrice]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (

    <section className="bg-[#F7F7F7] py-16 sm:py-20 lg:py-28 overflow-hidden">

      <div className="max-w-[1480px] mx-auto px-4 sm:px-6">

        {/* Heading */}
        <div
          data-aos="fade-up"
          className="text-center mb-16"
        >

          <h2 className="
            text-[34px]
            leading-[44px]
            sm:text-[45px]
            sm:leading-[60px]
            lg:text-[50px]
            lg:leading-[68px]
            font-normal
            text-[#1F1F1F]
            font-[Pochaevsk]
          ">

Luxury Rental Properties for Modern Living
          </h2>

          <p className="
            text-[#6F6F6F]
            text-[16px]
            leading-[30px]
            sm:text-[20px]
            sm:leading-[38px]
            max-w-[850px]
            mx-auto
            mt-5
            sm:mt-6
            font-[Poppins]
          ">

Explore premium apartments, elegant villas, and exclusive rental residences designed for comfort, lifestyle, and modern living.
          </p>

        </div>

        <div className="mb-12 grid grid-cols-1 gap-4 rounded-[6px] border border-[#E4E4E4] bg-white p-5 sm:grid-cols-2 lg:grid-cols-4">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search location, title or reference"
            aria-label="Search rental properties"
            className="min-h-[48px] border border-[#D9D9D9] px-4 font-[Poppins] text-[14px] outline-none focus:border-[#183346]"
          />
          <select
            value={propertyType}
            onChange={(event) => setPropertyType(event.target.value)}
            aria-label="Filter rentals by property type"
            className="min-h-[48px] border border-[#D9D9D9] bg-white px-4 font-[Poppins] text-[14px] outline-none focus:border-[#183346]"
          >
            <option value="">All property types</option>
            {propertyTypes.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
          <select
            value={minBedrooms}
            onChange={(event) => setMinBedrooms(event.target.value)}
            aria-label="Filter rentals by minimum bedrooms"
            className="min-h-[48px] border border-[#D9D9D9] bg-white px-4 font-[Poppins] text-[14px] outline-none focus:border-[#183346]"
          >
            <option value="">Any bedrooms</option>
            {[1, 2, 3, 4, 5].map((bedrooms) => <option key={bedrooms} value={bedrooms}>{bedrooms}+ bedrooms</option>)}
          </select>
          <input
            type="number"
            min="0"
            value={maxPrice}
            onChange={(event) => setMaxPrice(event.target.value)}
            placeholder="Max monthly price (€)"
            aria-label="Filter rentals by maximum monthly price"
            className="min-h-[48px] border border-[#D9D9D9] px-4 font-[Poppins] text-[14px] outline-none focus:border-[#183346]"
          />
        </div>

        {loading && <p className="py-16 text-center font-[Poppins] text-[#6F6F6F]">Loading rental properties...</p>}
        {error && <p className="py-16 text-center font-[Poppins] text-red-500">Unable to load rental properties. Please try again.</p>}
        {!loading && !error && filteredProperties.length === 0 && (
          <p className="py-16 text-center font-[Poppins] text-[#6F6F6F]">No rental properties match your search.</p>
        )}

        {!loading && !error && filteredProperties.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10">

          {visibleProperties.map((property, index) => (

            <Link
              key={property.Reference}
              to={`/rent-property/${property.slug}`}
              className="group block"
            >
              <div
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="
                  bg-white
                  border
                  border-[#E4E4E4]
                  rounded-[6px]
                  overflow-hidden
                  transition-all
                  duration-500
                  group
                  hover:bg-[#181A20]
                "
              >

              {/* Image */}
              <div className="relative overflow-hidden">

                <img
                  src={property.Pictures?.Picture?.[0]?.PictureURL || property.image_url || ""}
                  alt={property.PropertyType?.NameType || "Rental property"}
                  className="
                    w-full
                    h-[320px]
                    sm:h-[360px]
                    object-cover
                    transition-all
                    duration-700
                    group-hover:scale-105
                  "
                />

                {/* Top Icons */}
                <div className="absolute top-6 left-5 flex items-center gap-2">

                  <img
                    src={StarColor}
                    alt=""
                    className="w-[22px] h-[22px] sm:w-[24px] sm:h-[24px]"
                  />

                  <img
                    src={StarColor}
                    alt=""
                    className="w-[22px] h-[22px] sm:w-[24px] sm:h-[24px]"
                  />

                  <img
                    src={StarColor}
                    alt=""
                    className="w-[22px] h-[22px] sm:w-[24px] sm:h-[24px]"
                  />

                  <img
                    src={StarWhite}
                    alt=""
                    className="w-[22px] h-[22px] sm:w-[24px] sm:h-[24px]"
                  />

                  <img
                    src={StarWhite}
                    alt=""
                    className="w-[22px] h-[22px] sm:w-[24px] sm:h-[24px]"
                  />

                </div>

                {/* VR Icon */}
                <div className="absolute top-5 right-5">

                  <img
                    src={VrIcon}
                    alt=""
                    className="
                      w-[34px]
                      h-[34px]
                      sm:w-[38px]
                      sm:h-[38px]
                      transition-all
                      duration-500
                      group-hover:brightness-0
                      group-hover:invert
                    "
                  />

                </div>

              </div>

              {/* Content */}
              <div className="p-6 sm:p-7 transition-all duration-500">

                {/* Price */}
                <div className="flex items-end gap-2 mb-4 sm:mb-5">

                  <h3 className="
                    text-[28px]
                    sm:text-[30px]
                    font-normal
                    text-[#183346]
                    leading-none
                    font-[Pochaevsk]
                    transition-all
                    duration-500
                    group-hover:text-white
                  ">

                    €{Number(property.Price).toLocaleString()}

                  </h3>

                  <span className="
                    text-[#A1A1A1]
                    text-[15px]
                    sm:text-[18px]
                    mb-[2px]
                    font-[Poppins]
                    transition-all
                    duration-500
                    group-hover:text-white/70
                  ">
                    /month
                  </span>

                </div>

                {/* Title */}
                <h4 className="
                  text-[18px]
                  sm:text-[22px]
                  leading-[32px]
                  sm:leading-[38px]
                  font-normal
                  text-[#1F1F1F]
                  mb-4
                  font-[Pochaevsk]
                  transition-all
                  duration-500
                  group-hover:text-white
                ">

                    {property.PropertyType?.NameType || "Rental property"}

                </h4>

                {/* Location */}
                <p className="
                  text-[#A5A5A5]
                  text-[15px]
                  sm:text-[18px]
                  mb-5
                  font-[Poppins]
                  transition-all
                  duration-500
                  group-hover:text-white/70
                ">

                    {[property.Location, property.Province].filter(Boolean).join(", ")}

                </p>

                {/* Bottom Icons */}
                <div className="grid grid-cols-2 gap-5 mt-6">

                  {/* Feature 1 */}
                  <div className="flex items-center gap-3 sm:gap-4">

                    <img
                      src={BedIcon}
                      alt=""
                      className="
                        w-[22px]
                        h-[22px]
                        sm:w-[24px]
                        sm:h-[24px]
                        transition-all
                        duration-500
                        group-hover:brightness-0
                        group-hover:invert
                      "
                    />

                    <span className="
                      text-[14px]
                      sm:text-[16px]
                      text-[#4A4A4A]
                      font-[Poppins]
                      transition-all
                      duration-500
                      group-hover:text-white
                    ">

                      {property.Bedrooms || 0} Bedrooms

                    </span>

                  </div>

                  {/* Feature 2 */}
                  <div className="flex items-center gap-3 sm:gap-4">

                    <img
                      src={PersonIcon}
                      alt=""
                      className="
                        w-[22px]
                        h-[22px]
                        sm:w-[24px]
                        sm:h-[24px]
                        transition-all
                        duration-500
                        group-hover:brightness-0
                        group-hover:invert
                      "
                    />

                    <span className="
                      text-[14px]
                      sm:text-[16px]
                      text-[#4A4A4A]
                      font-[Poppins]
                      transition-all
                      duration-500
                      group-hover:text-white
                    ">

                      International Buyers

                    </span>

                  </div>

                  {/* Feature 3 */}
                  <div className="flex items-center gap-3 sm:gap-4">

                    <img
                      src={BatIcon}
                      alt=""
                      className="
                        w-[22px]
                        h-[22px]
                        sm:w-[24px]
                        sm:h-[24px]
                        transition-all
                        duration-500
                        group-hover:brightness-0
                        group-hover:invert
                      "
                    />

                    <span className="
                      text-[14px]
                      sm:text-[16px]
                      text-[#4A4A4A]
                      font-[Poppins]
                      transition-all
                      duration-500
                      group-hover:text-white
                    ">

                      {property.Bathrooms || 0} Bathrooms

                    </span>

                  </div>

                  {/* Feature 4 */}
                  <div className="flex items-center gap-3 sm:gap-4">

                    <img
                      src={VrIcon}
                      alt=""
                      className="
                        w-[22px]
                        h-[22px]
                        sm:w-[24px]
                        sm:h-[24px]
                        transition-all
                        duration-500
                        group-hover:brightness-0
                        group-hover:invert
                      "
                    />

                    <span className="
                      text-[14px]
                      sm:text-[16px]
                      text-[#4A4A4A]
                      font-[Poppins]
                      transition-all
                      duration-500
                      group-hover:text-white
                    ">

                      Investment Potential

                    </span>

                  </div>

                </div>

              </div>

            </div>

            </Link>

          ))}

        </div>}

        {!loading && !error && filteredProperties.length > 0 && <div className="mt-10 flex flex-col items-center justify-between gap-4 font-[Poppins] text-[14px] text-[#6F6F6F] sm:flex-row">
          <span>Page {page} of {totalPages} ({filteredProperties.length} properties)</span>
          <div className="flex gap-3">
            <button type="button" disabled={page === 1} onClick={() => setPage((current) => current - 1)} className="border border-[#183346] px-5 py-2 text-[#183346] transition-colors hover:bg-[#183346] hover:text-white disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
            <button type="button" disabled={page === totalPages} onClick={() => setPage((current) => current + 1)} className="border border-[#183346] px-5 py-2 text-[#183346] transition-colors hover:bg-[#183346] hover:text-white disabled:cursor-not-allowed disabled:opacity-40">Next</button>
          </div>
        </div>}

      </div>

    </section>

  );
};

export default PropertyListings;